import type { DeliveryGroupDraft, DeliveryGroupStopDraft, LegacyStopDraft } from './deliveryGroupTypes';
import { makeStopUid } from './deliveryGroupModel';
import { getOrderedStops } from './editorStopOrdering';
import { isSequentialStopsEditorModeEnabled } from './sequentialStopsEditorFlags';
import type { AllocationEdgeDraft, FromStopDraftByUid, QuantityDraftByCargo } from './executionDraftTypes';
import type { ExecutionTopologyPreview } from './executionTopologyHelpers';
import { listLoadingPointsFromTopology } from './executionTopologyHelpers';

export type ApplicationHydrationRef = {
  id: number;
  internalNumber?: string | null;
  loadingAddress?: string | null;
  unloadingAddress?: string | null;
  loadingContactPerson?: string | null;
  loadingPhone?: string | null;
  unloadingContactPerson?: string | null;
  unloadingPhone?: string | null;
  consigneeId?: number | null;
  consignee?: {
    name?: string | null;
    contactPerson?: string | null;
    phone?: string | null;
    address?: string | null;
  } | null;
  plannedLoadingDate?: string | null;
  plannedUnloadingDate?: string | null;
};

export type WarehouseHydrationRef = {
  id: number;
  name?: string;
  city?: string;
  address?: string;
  contactPerson?: string;
  phone?: string;
};

export type ConsigneeHydrationRef = {
  id: number;
  name?: string;
  address?: string | null;
  contactPerson?: string | null;
  phone?: string | null;
  city?: string | null;
};

export function hydrateLoadingStopFromApplication(
  stop: DeliveryGroupStopDraft,
  app: ApplicationHydrationRef
): DeliveryGroupStopDraft {
  return {
    ...stop,
    pointType: 'application',
    pointId: app.id,
    address: app.loadingAddress ?? stop.address ?? null,
    contactPerson: app.loadingContactPerson ?? stop.contactPerson ?? null,
    phone: app.loadingPhone ?? stop.phone ?? null,
    plannedAt: stop.plannedAt ?? app.plannedLoadingDate ?? null
  };
}

/** Customer unload endpoint: application id (Delivery Group canonical). */
export function hydrateUnloadingStopFromApplication(
  stop: DeliveryGroupStopDraft,
  app: ApplicationHydrationRef
): DeliveryGroupStopDraft {
  return {
    ...stop,
    pointType: 'application',
    pointId: app.id,
    address: app.unloadingAddress ?? app.consignee?.address ?? stop.address ?? null,
    contactPerson: app.unloadingContactPerson ?? app.consignee?.contactPerson ?? stop.contactPerson ?? null,
    phone: app.unloadingPhone ?? app.consignee?.phone ?? stop.phone ?? null,
    plannedAt: stop.plannedAt ?? app.plannedUnloadingDate ?? null
  };
}

/** @deprecated Use hydrateUnloadingStopFromApplication. Kept for import compatibility. */
export function hydrateUnloadingStopFromApplicationConsignee(
  stop: DeliveryGroupStopDraft,
  app: ApplicationHydrationRef
): DeliveryGroupStopDraft {
  return hydrateUnloadingStopFromApplication(stop, app);
}

export function hydrateStopFromWarehouse(
  stop: DeliveryGroupStopDraft,
  warehouse: WarehouseHydrationRef,
  force = false
): DeliveryGroupStopDraft {
  const address =
    warehouse.address ||
    `${warehouse.name || ''}${warehouse.city ? `, ${warehouse.city}` : ''}`.trim() ||
    stop.address;
  return {
    ...stop,
    pointType: 'warehouse',
    pointId: warehouse.id,
    address: force ? address ?? null : address ?? stop.address ?? null,
    contactPerson: force
      ? warehouse.contactPerson ?? null
      : warehouse.contactPerson ?? stop.contactPerson ?? null,
    phone: force ? warehouse.phone ?? null : warehouse.phone ?? stop.phone ?? null
  };
}

export function hydrateStopFromConsignee(
  stop: DeliveryGroupStopDraft,
  consignee: ConsigneeHydrationRef,
  force = false
): DeliveryGroupStopDraft {
  return {
    ...stop,
    pointType: 'consignee',
    pointId: consignee.id,
    address: force ? consignee.address ?? null : consignee.address ?? stop.address ?? null,
    contactPerson: force
      ? consignee.contactPerson ?? null
      : consignee.contactPerson ?? stop.contactPerson ?? null,
    phone: force ? consignee.phone ?? null : consignee.phone ?? stop.phone ?? null
  };
}

/** Только реальные точки погрузки группы (без synthetic primary). */
export function listRealLoadingPointsForGroup(
  group: DeliveryGroupDraft,
  topology?: ExecutionTopologyPreview | null
): Array<{
  key: string;
  position: number;
  label: string;
  stopUid: string;
}> {
  if (group.loadingStops.length === 0) return [];
  if (topology) {
    return listLoadingPointsFromTopology(group, topology);
  }

  if (isSequentialStopsEditorModeEnabled()) {
    return getOrderedStops([group])
      .filter((e) => e.stop.type === 'loading')
      .map((e, idx) => ({
        key: e.stop.uid,
        position: e.stop.position ?? idx,
        label: e.stop.address?.trim() || `Точка погрузки ${idx + 1}`,
        stopUid: e.stop.uid
      }));
  }

  return group.loadingStops.map((s, idx) => {
    const position =
      s.position != null
        ? s.position
        : group.isDefault && idx === 0
          ? 0
          : group.isDefault
            ? idx
            : idx + 1;
    const label = s.address?.trim() || `Точка погрузки ${idx + 1}`;
    return { key: s.uid, position, label, stopUid: s.uid };
  });
}

/** Проставить position на стопах групп из flatten + legacy stops. */
export function syncGroupStopPositionsFromFlatten(
  groups: DeliveryGroupDraft[],
  flattened: LegacyStopDraft[]
): DeliveryGroupDraft[] {
  const posByUid = new Map<string, number>();
  for (const s of flattened) {
    if (s.uid) posByUid.set(String(s.uid), s.position);
  }

  return groups.map((group) => {
    const loadingStops = group.loadingStops.map((s, idx) => {
      const nextPos =
        group.isDefault && idx === 0
          ? 0
          : posByUid.get(String(s.uid)) ?? s.position ?? idx + 1;
      if (nextPos === s.position) return s;
      return { ...s, position: nextPos };
    });
    const unloadingStops = group.unloadingStops.map((s) => {
      const nextPos = posByUid.get(String(s.uid)) ?? s.position;
      if (nextPos === s.position) return s;
      return { ...s, position: nextPos };
    });
    const primaryLoadingPos =
      loadingStops[0]?.position ?? (group.isDefault ? 0 : group.loadingPosition);
    if (
      primaryLoadingPos === group.loadingPosition &&
      loadingStops.every((s, i) => s === group.loadingStops[i]) &&
      unloadingStops.every((s, i) => s === group.unloadingStops[i])
    ) {
      return group;
    }
    return { ...group, loadingStops, unloadingStops, loadingPosition: primaryLoadingPos };
  });
}

export function resolveActiveLoadingStop(
  group: DeliveryGroupDraft | null | undefined,
  activeLoadingPosition: number
): DeliveryGroupStopDraft | null {
  if (!group) return null;
  const byPos = group.loadingStops.find((s) => s.position === activeLoadingPosition);
  if (byPos) return byPos;
  if (group.isDefault && activeLoadingPosition === 0 && group.loadingStops[0]) {
    return { ...group.loadingStops[0], position: 0 };
  }
  return group.loadingStops.find((_, idx) => idx + 1 === activeLoadingPosition) ?? null;
}

export function resolveCargoQueryFromLoadingStop(
  stop: DeliveryGroupStopDraft | null,
  group: DeliveryGroupDraft | null | undefined
): { loadingPointType?: 'warehouse' | 'application'; loadingPointId?: number } {
  if (stop?.pointType === 'warehouse' && stop.pointId) {
    return { loadingPointType: 'warehouse', loadingPointId: Number(stop.pointId) };
  }
  if (stop?.pointType === 'application' && stop.pointId) {
    return { loadingPointType: 'application', loadingPointId: Number(stop.pointId) };
  }
  if (group?.applicationId) {
    return { loadingPointType: 'application', loadingPointId: Number(group.applicationId) };
  }
  return {};
}

/** Грузы для текущей точки погрузки: скрывать полностью распределённые на других точках. */
function assignedQtyForCargo(params: {
  cargoId: number;
  activeLoadingStopUid: string;
  fromStopDraft: FromStopDraftByUid;
  quantityDraft: QuantityDraftByCargo;
  allocationEdges?: AllocationEdgeDraft[];
}): number {
  const id = params.cargoId;
  if (params.allocationEdges?.length) {
    return params.allocationEdges
      .filter((e) => Number(e.cargoId) === id && e.quantity > 0)
      .reduce((sum, e) => sum + e.quantity, 0);
  }
  return Number(params.quantityDraft[id] ?? 0);
}

export function filterCargoForLoadingExecution(params: {
  cargoRows: any[];
  activeLoadingStopUid: string;
  fromStopDraft: FromStopDraftByUid;
  quantityDraft: QuantityDraftByCargo;
  allocationEdges?: AllocationEdgeDraft[];
}): any[] {
  const { cargoRows, activeLoadingStopUid, fromStopDraft, quantityDraft, allocationEdges } = params;

  const hasEdgeAtLoading = (cargoId: number) =>
    allocationEdges?.some(
      (e) =>
        Number(e.cargoId) === cargoId &&
        e.fromStopUid === activeLoadingStopUid &&
        e.quantity > 0
    ) ?? false;

  return cargoRows
    .filter((cargo) => {
      const id = Number(cargo.id);
      const fromUid = fromStopDraft[id];
      const total = Number(cargo.availableQuantity ?? cargo.quantity ?? 0);
      const assignedQty = assignedQtyForCargo({
        cargoId: id,
        activeLoadingStopUid,
        fromStopDraft,
        quantityDraft,
        allocationEdges
      });

      if (hasEdgeAtLoading(id)) return true;
      if (fromUid === undefined) return true;
      if (fromUid === activeLoadingStopUid) return true;
      return assignedQty < total;
    })
    .map((cargo) => {
      const id = Number(cargo.id);
      const total = Number(cargo.availableQuantity ?? cargo.quantity ?? 0);
      const fromUid = fromStopDraft[id];
      const assignedAll = assignedQtyForCargo({
        cargoId: id,
        activeLoadingStopUid,
        fromStopDraft,
        quantityDraft,
        allocationEdges
      });
      const assignedHere = allocationEdges?.length
        ? allocationEdges
            .filter(
              (e) =>
                Number(e.cargoId) === id &&
                e.fromStopUid === activeLoadingStopUid &&
                e.quantity > 0
            )
            .reduce((s, e) => s + e.quantity, 0)
        : fromUid === activeLoadingStopUid
          ? Number(quantityDraft[id] ?? 0)
          : 0;

      if (fromUid === activeLoadingStopUid || hasEdgeAtLoading(id)) {
        const cap = Math.max(0, total - assignedAll + assignedHere);
        return {
          ...cargo,
          availableQuantity: cap > 0 ? cap : total,
          _assignedQtyHere: assignedHere
        };
      }
      if (fromUid !== undefined && fromUid !== activeLoadingStopUid) {
        const remaining = Math.max(0, total - assignedAll);
        return { ...cargo, availableQuantity: remaining, _assignedQtyHere: 0 };
      }
      return { ...cargo, availableQuantity: total, _assignedQtyHere: 0 };
    });
}

/** @deprecated position-based — use filterCargoForLoadingExecution with stopUid */
export function filterCargoForLoadingExecutionByPosition(params: {
  cargoRows: any[];
  activeLoadingPosition: number;
  fromStopDraft: Record<number, number | undefined>;
  quantityDraft: Record<number, number>;
}): any[] {
  return filterCargoForLoadingExecution({
    cargoRows: params.cargoRows,
    activeLoadingStopUid: `__legacy_pos_${params.activeLoadingPosition}`,
    fromStopDraft: Object.fromEntries(
      Object.entries(params.fromStopDraft).map(([k, v]) => [
        k,
        v === params.activeLoadingPosition ? `__legacy_pos_${v}` : v != null ? `__legacy_pos_${v}` : undefined
      ])
    ),
    quantityDraft: params.quantityDraft
  });
}

export type ExecutionValidationIssue = {
  code:
    | 'MISSING_LOADING_STOP'
    | 'MISSING_UNLOADING_STOP'
    | 'CROSS_GROUP'
    | 'INVALID_QTY'
    | 'QTY_EXCEEDS_AVAILABLE';
  message: string;
};

export function validateExecutionBindings(params: {
  groups: DeliveryGroupDraft[];
  selectedIds: number[];
  fromStopDraft: Record<number, number | undefined>;
  toStopDraft: Record<number, number | undefined>;
  quantityDraft: Record<number, number>;
  activeGroupUid?: string | null;
}): ExecutionValidationIssue[] {
  const issues: ExecutionValidationIssue[] = [];
  const groupByLoadingPos = new Map<number, string>();

  for (const group of params.groups) {
    const points = listRealLoadingPointsForGroup(group);
    for (const p of points) {
      groupByLoadingPos.set(p.position, group.uid);
    }
  }

  for (const cargoId of params.selectedIds) {
    const qty = Number(params.quantityDraft[cargoId] ?? 0);
    if (qty <= 0) continue;
    const fromPos = params.fromStopDraft[cargoId];
    const toPos = params.toStopDraft[cargoId];
    if (fromPos === undefined) {
      issues.push({
        code: 'MISSING_LOADING_STOP',
        message: `Груз #${cargoId}: не указана точка погрузки`
      });
    }
    if (toPos === undefined) {
      issues.push({
        code: 'MISSING_UNLOADING_STOP',
        message: `Груз #${cargoId}: не указана точка выгрузки`
      });
    }
    if (
      params.activeGroupUid &&
      fromPos !== undefined &&
      groupByLoadingPos.get(fromPos) &&
      groupByLoadingPos.get(fromPos) !== params.activeGroupUid
    ) {
      issues.push({
        code: 'CROSS_GROUP',
        message: `Нельзя привязать груз #${cargoId} к точке погрузки из другой группы маршрута`
      });
    }
  }

  return issues;
}

export type MainRouteStopRef = {
  id?: number | null;
  uid?: string;
  pointType?: string | null;
  pointId?: number | null;
  address?: string | null;
  contactPerson?: string | null;
  phone?: string | null;
  plannedAt?: string | null;
  actualAt?: string | null;
  status?: string | null;
};

/**
 * A draft is the main stop itself when ids match, or when uids match and ids do not contradict.
 * uid is execution identity (route_stops.execution_stop_uid is globally unique).
 */
function isSameLogicalMainStop(stop: DeliveryGroupStopDraft, main: MainRouteStopRef): boolean {
  if (stop.id != null && main.id != null) return stop.id === main.id;
  return Boolean(main.uid) && stop.uid === main.uid;
}

function collectGroupStopUids(groups: DeliveryGroupDraft[]): Set<string> {
  const uids = new Set<string>();
  for (const g of groups) {
    for (const s of [...g.loadingStops, ...g.unloadingStops]) uids.add(s.uid);
  }
  return uids;
}

/** Reuse the main stop uid only when no other draft owns it. */
function resolveMainStopUid(main: MainRouteStopRef, usedUids: Set<string>): string {
  return main.uid && !usedUids.has(main.uid) ? main.uid : makeStopUid();
}

/** Восстановление основной точки погрузки (position 0) в default group при edit. */
export function ensureDefaultGroupMainLoadingStop(
  groups: DeliveryGroupDraft[],
  mainLoading: MainRouteStopRef | null | undefined
): DeliveryGroupDraft[] {
  if (!mainLoading) return groups;
  const usedUids = collectGroupStopUids(groups);

  return groups.map((group) => {
    if (!group.isDefault) return group;

    const positionZeroIdx = group.loadingStops.findIndex((s) => s.position === 0);
    const existingIdx =
      positionZeroIdx >= 0
        ? positionZeroIdx
        : group.loadingStops.findIndex((s) => isSameLogicalMainStop(s, mainLoading));
    if (existingIdx >= 0) {
      const existing = group.loadingStops[existingIdx];
      const merged: DeliveryGroupStopDraft = {
        ...existing,
        id: existing.id ?? mainLoading.id ?? null,
        pointType: (existing.pointType ?? mainLoading.pointType ?? null) as DeliveryGroupStopDraft['pointType'],
        pointId: existing.pointId ?? mainLoading.pointId ?? group.applicationId ?? null,
        address: existing.address ?? mainLoading.address ?? null,
        contactPerson: existing.contactPerson ?? mainLoading.contactPerson ?? null,
        phone: existing.phone ?? mainLoading.phone ?? null,
        plannedAt: existing.plannedAt ?? mainLoading.plannedAt ?? null,
        actualAt: existing.actualAt ?? mainLoading.actualAt ?? null,
        status: existing.status ?? mainLoading.status ?? null,
        position: 0
      };
      const loadingStops =
        positionZeroIdx >= 0
          ? group.loadingStops.map((s, i) => (i === existingIdx ? merged : s))
          : [merged, ...group.loadingStops.filter((_, i) => i !== existingIdx)];
      return { ...group, loadingStops, loadingPosition: 0 };
    }

    const stop: DeliveryGroupStopDraft = {
      uid: resolveMainStopUid(mainLoading, usedUids),
      id: mainLoading.id ?? null,
      groupUid: group.uid,
      type: 'loading',
      position: 0,
      pointType: (mainLoading.pointType ?? (group.applicationId ? 'application' : 'warehouse')) as DeliveryGroupStopDraft['pointType'],
      pointId: mainLoading.pointId ?? group.applicationId ?? null,
      address: mainLoading.address ?? null,
      contactPerson: mainLoading.contactPerson ?? null,
      phone: mainLoading.phone ?? null,
      plannedAt: mainLoading.plannedAt ?? null,
      actualAt: mainLoading.actualAt ?? null,
      notes: null,
      status: mainLoading.status ?? null
    };
    return { ...group, loadingStops: [stop, ...group.loadingStops], loadingPosition: 0 };
  });
}

/** Восстановление основной точки выгрузки default group при edit (form contract). */
export function ensureDefaultGroupMainUnloadingStop(
  groups: DeliveryGroupDraft[],
  mainUnloading: MainRouteStopRef | null | undefined
): DeliveryGroupDraft[] {
  if (!mainUnloading) return groups;
  const usedUids = collectGroupStopUids(groups);

  return groups.map((group) => {
    if (!group.isDefault) return group;
    const hasMainUnload = group.unloadingStops.some((s) => isSameLogicalMainStop(s, mainUnloading));
    if (hasMainUnload) return group;

    const stop: DeliveryGroupStopDraft = {
      uid: resolveMainStopUid(mainUnloading, usedUids),
      id: mainUnloading.id ?? null,
      groupUid: group.uid,
      type: 'unloading',
      pointType: (mainUnloading.pointType ??
        (group.applicationId ? 'application' : 'consignee')) as DeliveryGroupStopDraft['pointType'],
      pointId: mainUnloading.pointId ?? group.applicationId ?? null,
      address: mainUnloading.address ?? null,
      contactPerson: mainUnloading.contactPerson ?? null,
      phone: mainUnloading.phone ?? null,
      plannedAt: mainUnloading.plannedAt ?? null,
      actualAt: mainUnloading.actualAt ?? null,
      notes: null,
      status: mainUnloading.status ?? null
    };
    return { ...group, unloadingStops: [stop, ...group.unloadingStops] };
  });
}

function stopNeedsHydration(
  stop: DeliveryGroupStopDraft,
  hydrated: DeliveryGroupStopDraft
): boolean {
  return (
    stop.address !== hydrated.address ||
    stop.contactPerson !== hydrated.contactPerson ||
    stop.phone !== hydrated.phone ||
    stop.plannedAt !== hydrated.plannedAt ||
    stop.pointId !== hydrated.pointId
  );
}

export function maybeHydrateLoadingFromApplication(
  stop: DeliveryGroupStopDraft,
  app: ApplicationHydrationRef
): DeliveryGroupStopDraft | null {
  const hydrated = hydrateLoadingStopFromApplication(stop, app);
  return stopNeedsHydration(stop, hydrated) ? hydrated : null;
}

export function maybeHydrateUnloadingFromApplication(
  stop: DeliveryGroupStopDraft,
  app: ApplicationHydrationRef
): DeliveryGroupStopDraft | null {
  if (stop.pointType === 'consignee') return null;
  const hydrated = hydrateUnloadingStopFromApplication(stop, app);
  return stopNeedsHydration(stop, hydrated) ? hydrated : null;
}

/** @deprecated Use maybeHydrateUnloadingFromApplication. Does not mutate legacy consignee stops. */
export function maybeHydrateUnloadingFromApplicationConsignee(
  stop: DeliveryGroupStopDraft,
  app: ApplicationHydrationRef
): DeliveryGroupStopDraft | null {
  return maybeHydrateUnloadingFromApplication(stop, app);
}

/** Семантика выгрузки (audit reference, без изменения backend). */
export const UNLOAD_EXECUTION_SEMANTICS = {
  application: {
    cargoStatus: 'delivered',
    currentWarehouseId: null,
    warehouseReceive: false
  },
  consignee: {
    cargoStatus: 'delivered',
    currentWarehouseId: null,
    warehouseReceive: false
  },
  warehouse: {
    cargoStatus: 'on_warehouse',
    currentWarehouseId: 'stop.pointId',
    warehouseReceive: true
  }
} as const;
